import { Component, OnInit, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../core/environment';

interface ReportOption {
  key: string;
  title: string;
  description: string;
  endpoint: string;
}

@Component({
  selector: 'app-reports',
  standalone: true,
  template: `
    <div class="ops-page">
      <div class="ops-hero reports-hero">
        <div><span>Analytics</span><h2>Reports</h2><p>Student, guest play, attendance, fees, and due collection reports for admin review.</p></div>
        <button type="button" (click)="load(activeReport())">Run Report</button>
      </div>

      <section class="report-tabs">
        @for (report of reports; track report.key) {
          <button type="button" [class.active]="activeReport() === report.key" (click)="load(report.key)">
            <strong>{{ report.title }}</strong>
            <span>{{ report.description }}</span>
          </button>
        }
      </section>

      <section class="ops-stats">
        <article><span>Rows</span><strong>{{ rows().length }}</strong><p>Current result</p></article>
        <article><span>Report</span><strong>{{ currentTitle() }}</strong><p>Selected dataset</p></article>
        <article><span>Export</span><strong>Excel</strong><p>Future-ready action</p></article>
        <article><span>Print</span><strong>PDF</strong><p>Future-ready action</p></article>
      </section>

      <article class="ops-card">
        <div class="card-title">
          <div><h3>{{ currentTitle() }}</h3><p>Showing first-level fields returned by the API.</p></div>
          <span>{{ rows().length }} rows</span>
        </div>
        <div class="report-table-wrap">
          <table>
            <thead>
              <tr>
                @for (column of columns(); track column) {
                  <th>{{ column }}</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of rows(); track row['id'] ?? $index) {
                <tr>
                  @for (column of columns(); track column) {
                    <td>{{ format(row[column]) }}</td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
      </article>
    </div>
  `
})
export class ReportsComponent implements OnInit {
  private readonly http = inject(HttpClient);
  readonly reports: ReportOption[] = [
    { key: 'students', title: 'Student Report', description: 'Admissions and profiles', endpoint: 'students' },
    { key: 'guest-play', title: 'Guest Play Report', description: 'Walk-in bookings', endpoint: 'guest-play' },
    { key: 'attendance', title: 'Attendance Report', description: 'Daily movement', endpoint: 'attendance' },
    { key: 'fee-collection', title: 'Fees Collection', description: 'Payment history', endpoint: 'fee-collection' },
    { key: 'due-fees', title: 'Due Fees', description: 'Pending balances', endpoint: 'due-fees' }
  ];
  readonly activeReport = signal('students');
  readonly rows = signal<Record<string, unknown>[]>([]);
  readonly columns = signal<string[]>([]);

  ngOnInit(): void { this.load('students'); }

  load(key: string): void {
    const report = this.reports.find((x) => x.key === key) ?? this.reports[0];
    this.activeReport.set(report.key);
    this.http.get<Record<string, unknown>[]>(`${environment.apiUrl}/reports/${report.endpoint}`).subscribe((rows) => {
      this.rows.set(rows ?? []);
      this.columns.set(this.pickColumns(rows ?? []));
    });
  }

  currentTitle(): string {
    return this.reports.find((x) => x.key === this.activeReport())?.title ?? 'Report';
  }

  format(value: unknown): string {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return '[details]';
    return String(value);
  }

  private pickColumns(rows: Record<string, unknown>[]): string[] {
    const first = rows[0];
    if (!first) return ['No data'];
    return Object.keys(first).filter((key) => !Array.isArray(first[key])).slice(0, 8);
  }
}

